import { createRoot } from 'react-dom/client';
import type {
    ColDef,
    ColGroupDef,
    GetContextMenuItemsParams,
    MenuItemDef,
    SelectionChangedEvent,
} from '@ag-grid-community/core';
import { type AgGridReact } from '@ag-grid-community/react';
import { type ReactNode, type RefObject } from 'react';

export const svgIconToDomNode = (icon: ReactNode): HTMLDivElement => {
    const domNode = document.createElement('div');
    createRoot(domNode).render(icon);
    return domNode;
};

export function getExcludedContextMenuItems(
    params: GetContextMenuItemsParams,
    exclude: string[] = [],
): (string | MenuItemDef)[] {
    return params.defaultItems?.filter((value) => {
        return !exclude.includes(value);
    })!;
}

/**
 * This function can be used to generate an onSelectionsChanged function for details grids
 * that automatically deselects any selected values in other details grids and the master grid
 */
export const onDetailsSelectionChangedGenerator = <T, T2>(
    onSelectionsChanged?: (event: SelectionChangedEvent<T>) => void,
    parentGridRef?: RefObject<AgGridReact<T2> | null>,
): ((event: SelectionChangedEvent<T>) => void) => {
    return (selectionChangedEvent: SelectionChangedEvent<T>) => {
        onSelectionsChanged?.(selectionChangedEvent);

        const selectedRows = selectionChangedEvent.api.getSelectedRows();
        if (!selectedRows.length) {
            return;
        }

        parentGridRef?.current?.api.deselectAll();
        parentGridRef?.current?.api.forEachDetailGridInfo((detailGrid) => {
            if (
                selectionChangedEvent.api.getGridId() !==
                detailGrid.api?.getGridId()
            ) {
                detailGrid.api?.deselectAll();
            }
        });
    };
};

/**
 * Extrapolates the column ids from the column definitions that CAN be filtered
 */
export function getFilterableColumnIds(
    colDefs: (ColDef | ColGroupDef)[],
): string[] {
    return colDefs.reduce<string[]>((accum, colDef) => {
        if (!isColumnFilterable(colDef)) {
            return accum;
        }

        if ('children' in colDef) {
            return [...accum, ...getFilterableColumnIds(colDef.children)];
        }

        if (colDef.field) {
            return [...accum, colDef.field];
        }

        return accum;
    }, []);
}

function isColumnFilterable(colDef: ColDef): boolean {
    return colDef.filter !== false;
}
